import GithubSlugger from 'github-slugger'
import type { TocItem } from './types'

/**
 * Extract h2/h3 headings from a raw markdown body for TOC generation.
 *
 * rehype-slug runs github-slugger over the *rendered* heading text node,
 * meaning markdown inline code spans (backtick-wrapped) have already been
 * converted to their inner text by the time rehype-slug sees them.  We
 * replicate that strip here so the anchor ids we generate match the ids
 * rehype-slug inserts into the rendered HTML.
 *
 * A fresh GithubSlugger is created per call so duplicate headings within a
 * document get the same `-1`, `-2` suffixes that rehype-slug would assign.
 *
 * We deliberately do NOT try to parse markdown fully — just grep for lines
 * starting with "## " or "### " which is sufficient for well-formed docs.
 */
function stripInlineCode(text: string): string {
  // Replace `code span` with its inner text (no backticks).
  // This mirrors the rendered text that rehype-slug receives.
  return text.replace(/`([^`]*)`/g, '$1')
}

export function extractTocItems(body: string): TocItem[] {
  const slugger = new GithubSlugger()
  const items: TocItem[] = []
  for (const line of body.split('\n')) {
    const h2 = /^##\s+(.+)$/.exec(line)
    if (h2 && h2[1]) {
      const text = h2[1].trim()
      items.push({ level: 2, text, id: slugger.slug(stripInlineCode(text)) })
      continue
    }
    const h3 = /^###\s+(.+)$/.exec(line)
    if (h3 && h3[1]) {
      const text = h3[1].trim()
      items.push({ level: 3, text, id: slugger.slug(stripInlineCode(text)) })
    }
  }
  return items
}
